"use client";

import React from "react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { Info } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"; // Import CardTitle here

interface PreexistingConditionSelectionProps {
  preexistingCoverage: boolean | null;
  setPreexistingCoverage: (value: boolean | null) => void;
  error: string;
  isLoading: boolean;
  groupRef: React.RefObject<HTMLDivElement>;
  petName: string;
  petType: "cat" | "dog";
}

const PreexistingConditionSelection: React.FC<PreexistingConditionSelectionProps> = ({
  preexistingCoverage,
  setPreexistingCoverage,
  error,
  isLoading,
  groupRef,
  petName,
  petType,
}) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-2" ref={groupRef}>
      <div className="flex items-center gap-2">
        <Label htmlFor="preexisting-coverage">
          {petName ? t("petDetails.preexistingConditionsQuestion", { petName: petName }) :  t("petDetails.preexistingConditionsQuestionUndefined")}
        </Label>
        <Dialog>
          <DialogTrigger asChild>
            <Button variant="ghost" size="icon" className="h-6 w-6">
              <Info className="h-4 w-4 text-gray-500" />
              <span className="sr-only">{t("common.moreInfo")}</span>
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("petDetails.preexistingConditionsInfoTitle")}</DialogTitle>
              <DialogDescription>
                {t("petDetails.preexistingConditionsInfoDescription")}
              </DialogDescription>
            </DialogHeader>
          </DialogContent>
        </Dialog>
      </div>
      <div className="flex gap-2">
        <Button
          variant={preexistingCoverage === true ? "default" : "outline"}
          onClick={() => setPreexistingCoverage(true)}
          className={cn(
            "flex-1",
            preexistingCoverage === true && "bg-primary text-primary-foreground"
          )}
          disabled={isLoading}
        >
          {t("common.yes")}
        </Button>
        <Button
          variant={preexistingCoverage === false ? "default" : "outline"}
          onClick={() => setPreexistingCoverage(false)}
          className={cn(
            "flex-1",
            preexistingCoverage === false && "bg-primary text-primary-foreground"
          )}
          disabled={isLoading}
        >
          {t("common.no")}
        </Button>
      </div>
      {/* The Alert box is now a Card with updated styling */}
      <Card className="bg-secondary/10 dark:bg-secondary/20 border-secondary/30 dark:border-secondary/40 shadow-none">
        <CardHeader className="p-4 pb-2">
          <CardTitle className="text-lg font-semibold">
            {t("pet-info-preexisting-info-header")}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0 text-sm text-muted-foreground">
          {t("pet-info-preexisting-info-content")}
        </CardContent>
      </Card>
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
};

export default PreexistingConditionSelection;
